import { useCallback, useEffect, useRef, useState } from 'react';
import { getWithAuth } from 'src/api';

export const SECTION_KEYS = {
  PMP: 'pmp',
  PTP: 'ptp',
};

const ALL_SECTIONS = Object.values(SECTION_KEYS);

const SECTION_ENDPOINTS = {
  [SECTION_KEYS.PMP]: 'network_bom_pmp',
  [SECTION_KEYS.PTP]: 'network_bom_ptp',
};

// Placeholder data used for sections that don't exist in this project.
const EMPTY_SECTION_DATA = {
  [SECTION_KEYS.PMP]: { ap_bom: [], sm_bom: [] },
  [SECTION_KEYS.PTP]: { ptp_bom: [] },
};

/**
 * Manages fetching, caching, and polling for all BOM sections.
 *
 * @param projectId        - Active project ID.
 * @param hasPmpData       - Whether the project has PMP equipment.
 * @param hasPtpData       - Whether the project has PTP links.
 * @param isCalculating    - True while the project is recalculating; defers all fetching.
 * @param networkBomReadyTick - Timestamp bumped by the websocket when a BOM section completes.
 *
 * @returns { sectionData, loading, readyState }
 */
export function useBomLoader({
  projectId,
  hasPmpData,
  hasPtpData,
  isCalculating,
  networkBomReadyTick,
}) {
  const sectionRequired = {
    [SECTION_KEYS.PMP]: hasPmpData,
    [SECTION_KEYS.PTP]: hasPtpData,
  };

  const [sectionData, setSectionData] = useState({
    [SECTION_KEYS.PMP]: null,
    [SECTION_KEYS.PTP]: null,
  });
  const [loading, setLoading] = useState(false);
  const [readyState, setReadyState] = useState({
    [SECTION_KEYS.PMP]: false,
    [SECTION_KEYS.PTP]: false,
  });

  // readyRef mirrors readyState so callbacks always see the current value
  // without being re-created every render.
  const readyRef = useRef({ ...readyState });
  const fetchInFlight = useRef(false);
  const pendingRefresh = useRef(false);
  const refreshStartedAt = useRef(0);

  const setReady = useCallback((section, value) => {
    readyRef.current = { ...readyRef.current, [section]: value };
    setReadyState((prev) => ({ ...prev, [section]: value }));
  }, []);

  const hasUnresolved = useCallback(
    () => ALL_SECTIONS.some((s) => sectionRequired[s] && !readyRef.current[s]),
    // sectionRequired is a new object each render, so spread the relevant flags
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasPmpData, hasPtpData]
  );

  const resetSections = useCallback(() => {
    setLoading(true);
    ALL_SECTIONS.forEach((s) => setReady(s, !sectionRequired[s]));
    setSectionData(
      Object.fromEntries(
        ALL_SECTIONS.map((s) => [
          s,
          sectionRequired[s] ? null : EMPTY_SECTION_DATA[s],
        ])
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPmpData, hasPtpData, setReady]);

  const fetchSection = useCallback(
    (section) =>
      getWithAuth(`project/${projectId}/${SECTION_ENDPOINTS[section]}`)
        .then((res) => {
          setSectionData((prev) => ({ ...prev, [section]: res ?? {} }));
          const ready = res?.status === 'ready';
          setReady(section, ready);
          return ready;
        })
        .catch(() => {
          setReady(section, false);
          return false;
        }),
    [projectId, setReady]
  );

  const loadBom = useCallback(
    (doReset = false) => {
      if (fetchInFlight.current) {
        pendingRefresh.current = true;
        return;
      }

      if (!doReset && !hasUnresolved()) {
        setLoading(false);
        return;
      }

      if (doReset) {
        refreshStartedAt.current = Date.now();
        resetSections();
      }

      const toFetch = ALL_SECTIONS.filter(
        (s) => sectionRequired[s] && (doReset || !readyRef.current[s])
      );

      if (toFetch.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      fetchInFlight.current = true;
      Promise.all(toFetch.map(fetchSection))
        .then((results) => setLoading(!results.every(Boolean)))
        .finally(() => {
          fetchInFlight.current = false;
          if (pendingRefresh.current) {
            pendingRefresh.current = false;
            hasUnresolved() ? loadBom(false) : setLoading(false);
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchSection, hasUnresolved, resetSections, hasPmpData, hasPtpData]
  );

  // Trigger on mount and whenever the "calculating" state clears.
  useEffect(() => {
    if (isCalculating) {
      // Show loaders but defer all API calls until recalculation finishes.
      setLoading(true);
      return;
    }
    loadBom(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalculating]);

  // Re-poll when the websocket signals a BOM section has completed.
  useEffect(() => {
    if (!loading || !networkBomReadyTick) return;
    if (networkBomReadyTick <= refreshStartedAt.current) return; // stale tick
    if (!hasUnresolved()) return;
    loadBom(false);
  }, [loading, networkBomReadyTick, loadBom, hasUnresolved]);

  return { sectionData, loading, readyState };
}
